import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconPlayerPause,
  IconPlayerPlay,
  IconAd,
} from '@tabler/icons-react';
import type { Advertiser } from '../types';
import {
  getAdvertisers,
  createAdvertiser,
  updateAdvertiser,
  toggleAdvertiserActive,
  deleteAdvertiser,
} from '../services/advertisersService';

interface FormState {
  name: string;
  isActive: boolean;
}

const DEFAULT_FORM: FormState = { name: '', isActive: true };

export default function AdvertisersPage() {
  const navigate = useNavigate();
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Advertiser | null>(null);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    loadAdvertisers();
  }, []);

  async function loadAdvertisers() {
    try {
      setLoading(true);
      setAdvertisers(await getAdvertisers());
    } catch (err) {
      console.error('Error loading advertisers:', err);
    } finally {
      setLoading(false);
    }
  }

  function openAdd() {
    setEditing(null);
    setForm(DEFAULT_FORM);
    setFormError(null);
    setShowModal(true);
  }

  function openEdit(advertiser: Advertiser) {
    setEditing(advertiser);
    setForm({ name: advertiser.name, isActive: advertiser.isActive });
    setFormError(null);
    setShowModal(true);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      setFormError('Name is required');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        await updateAdvertiser(editing.id, { name: form.name.trim(), isActive: form.isActive });
      } else {
        await createAdvertiser({ name: form.name.trim(), isActive: form.isActive });
      }
      setShowModal(false);
      await loadAdvertisers();
    } catch (err) {
      console.error('Error saving advertiser:', err);
      setFormError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleToggle(advertiser: Advertiser) {
    try {
      await toggleAdvertiserActive(advertiser.id, advertiser.isActive);
      await loadAdvertisers();
    } catch (err) {
      console.error('Error toggling advertiser:', err);
      alert('Failed to update status');
    }
  }

  async function handleDelete(advertiser: Advertiser) {
    if (
      !confirm(
        `Delete advertiser "${advertiser.name}"? This will also delete all their campaigns and ad images.`
      )
    )
      return;
    try {
      await deleteAdvertiser(advertiser.id);
      await loadAdvertisers();
    } catch (err) {
      console.error('Error deleting advertiser:', err);
      alert('Failed to delete advertiser');
    }
  }

  if (loading) {
    return (
      <div className="loading-spinner">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="page-header">
      <div className="row g-2 align-items-center">
        <div className="col">
          <div className="page-pretitle">Advertising</div>
          <h2 className="page-title">Advertisers</h2>
        </div>
        <div className="col-auto">
          <button onClick={openAdd} className="btn btn-primary">
            <IconPlus className="icon" />
            Add Advertiser
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card mt-3">
        <div className="table-responsive">
          <table className="table table-vcenter card-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Created</th>
                <th className="w-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {advertisers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted py-4">
                    No advertisers yet
                  </td>
                </tr>
              ) : (
                advertisers.map(advertiser => (
                  <tr key={advertiser.id}>
                    <td>{advertiser.name}</td>
                    <td>
                      <span
                        className={`badge text-white ${advertiser.isActive ? 'bg-success' : 'bg-secondary'}`}
                      >
                        {advertiser.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="text-muted">
                      {new Date(advertiser.createdAt).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="btn-list flex-nowrap">
                        <button
                          onClick={() => navigate(`/advertisers/${advertiser.id}/campaigns`)}
                          className="btn btn-icon"
                          title="Manage Campaigns"
                        >
                          <IconAd className="icon" />
                        </button>
                        <button
                          onClick={() => handleToggle(advertiser)}
                          className="btn btn-icon"
                          title={advertiser.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {advertiser.isActive ? (
                            <IconPlayerPause className="icon" />
                          ) : (
                            <IconPlayerPlay className="icon" />
                          )}
                        </button>
                        <button
                          onClick={() => openEdit(advertiser)}
                          className="btn btn-icon"
                          title="Edit"
                        >
                          <IconPencil className="icon" />
                        </button>
                        <button
                          onClick={() => handleDelete(advertiser)}
                          className="btn btn-icon text-danger"
                          title="Delete"
                        >
                          <IconTrash className="icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div
          className="modal modal-blur fade show"
          tabIndex={-1}
          role="dialog"
          style={{ display: 'block' }}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editing ? 'Edit Advertiser' : 'New Advertiser'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                {formError && (
                  <div className="alert alert-danger alert-dismissible mb-3">{formError}</div>
                )}
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Advertiser name"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    autoFocus
                  />
                </div>
                <div className="mb-3">
                  <label className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={form.isActive}
                      onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                    />
                    <span className="form-check-label">Active</span>
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-link link-secondary me-auto"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
